import type { AppStore } from "@/store/AppStore";
import type {
  SearchCollectionsInput,
  SearchCollectionsOutput,
} from "@mem-client/ai-toolkit/contract";
import {
  AiToolkitToolName,
  searchCollectionsInputSchema,
  searchCollectionsOutputSchema,
} from "@mem-client/ai-toolkit/contract";
import type { AiToolkitToolDefinition } from "@/modules/ai-toolkit/tools/types";
import {
  DEFAULT_AI_TOOLKIT_SEARCH_LIMIT,
  normalizeAiToolkitSearchQuery,
} from "@/modules/ai-toolkit/tools/utils/local-search-helpers";
import { buildCollection } from "@/modules/ai-toolkit/tools/collections/collection-tools.helpers";
import { CollectionSearchReturnType } from "@/store/collections/types";

const buildSearchFilter = ({
  input,
}: {
  input: SearchCollectionsInput;
}): { isShared?: boolean; isOwnedByMe?: boolean } | undefined => {
  const filter: { isShared?: boolean; isOwnedByMe?: boolean } = {};

  if (input.isShared !== undefined) {
    filter.isShared = input.isShared;
  }
  if (input.isOwnedByMe !== undefined) {
    filter.isOwnedByMe = input.isOwnedByMe;
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
};

export const searchCollections = async ({
  store,
  input,
}: {
  store: AppStore;
  input: SearchCollectionsInput;
}): Promise<SearchCollectionsOutput> => {
  const collections = await store.collections.search({
    query: normalizeAiToolkitSearchQuery(input.query),
    filter: buildSearchFilter({ input }),
    returns: CollectionSearchReturnType.CollectionObservable,
    limit: input.limit ?? DEFAULT_AI_TOOLKIT_SEARCH_LIMIT,
  });

  return {
    collections: await Promise.all(collections.map(collection => buildCollection({ collection }))),
  };
};

export const searchCollectionsTool: AiToolkitToolDefinition<
  AiToolkitToolName.SearchCollections,
  SearchCollectionsInput,
  SearchCollectionsOutput
> = {
  name: AiToolkitToolName.SearchCollections,
  inputSchema: searchCollectionsInputSchema,
  outputSchema: searchCollectionsOutputSchema,
  strict: true,
  execute: searchCollections,
};
